import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Smartphone,
  Download,
  CheckCircle2,
  Zap,
  BookmarkCheck,
  Compass,
  MoreVertical,
  Share,
  PlusSquare,
  Sparkles,
  Info,
} from "lucide-react";

export default function AppInstallSection() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      {/* Horizontal Section Card on Desktop, Responsive on Mobile */}
      <Card className="rounded-2xl border border-border/60 bg-gradient-to-r from-card via-card to-primary/5 p-5 md:p-6 shadow-sm hover:border-primary/30 transition-all duration-300">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
          {/* Left: Icon & Text Container */}
          <div className="flex items-start md:items-center gap-4 flex-1">
            <div className="h-12 w-12 shrink-0 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-xs">
              <Smartphone className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-foreground">
                  Bawa Weddly ke Layar Utamamu
                </h3>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                  <Sparkles className="h-2.5 w-2.5" /> Web App
                </span>
              </div>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed max-w-2xl">
                Rencanakan hari spesialmu dengan lebih mudah. Tambahkan Weddly ke perangkatmu untuk akses cepat kapan pun kamu membutuhkannya.
              </p>
            </div>
          </div>

          {/* Right: CTA Button */}
          <div className="w-full md:w-auto shrink-0 pt-2 md:pt-0">
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              className="w-full md:w-auto rounded-full px-6 py-2.5 font-semibold text-xs md:text-sm bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow-md transition-all duration-200 gap-2"
            >
              <Download className="h-4 w-4" />
              <span>Panduan Instalasi</span>
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal Popup Panduan Instalasi */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden rounded-2xl border-border/80 shadow-2xl max-h-[90vh] flex flex-col">
          {/* Fixed Header */}
          <DialogHeader className="p-5 sm:p-6 pb-4 border-b border-border/50 bg-background/95 backdrop-blur-sm shrink-0">
            <div className="flex items-center gap-3.5">
              <img
                src="/weddly-logo.png"
                alt="Weddly Logo"
                className="h-11 w-11 object-contain rounded-xl border border-border/40 p-1 bg-white shadow-xs"
              />
              <div className="text-left">
                <DialogTitle className="text-xl font-serif font-bold text-foreground flex items-center gap-2">
                  Weddly
                </DialogTitle>
                <p className="text-xs font-mono text-muted-foreground mt-0.5">
                  weddly.my.id
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Scrollable Content Body */}
          <div className="p-5 sm:p-6 overflow-y-auto space-y-6 flex-1 text-left">
            {/* 3 Quick Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-xl bg-muted/40 border border-border/60 text-center space-y-1">
                <div className="inline-flex p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mb-1">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <h4 className="text-xs font-bold text-foreground">Gratis Digunakan</h4>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Tak perlu biaya instalasi.
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-muted/40 border border-border/60 text-center space-y-1">
                <div className="inline-flex p-2 rounded-lg bg-primary/10 text-primary mb-1">
                  <Zap className="h-4 w-4" />
                </div>
                <h4 className="text-xs font-bold text-foreground">Ringan &amp; Praktis</h4>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Akses mudah tanpa unduhan dari toko aplikasi.
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-muted/40 border border-border/60 text-center space-y-1">
                <div className="inline-flex p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 mb-1">
                  <BookmarkCheck className="h-4 w-4" />
                </div>
                <h4 className="text-xs font-bold text-foreground">Siap di Layar Utama</h4>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Buka Weddly langsung dari ikon perangkatmu.
                </p>
              </div>
            </div>

            {/* Interactive Tabs (Android vs iPhone) */}
            <Tabs defaultValue="android" className="w-full">
              <TabsList className="grid grid-cols-2 w-full h-11 p-1 bg-muted rounded-xl">
                <TabsTrigger
                  value="android"
                  className="rounded-lg text-xs sm:text-sm font-semibold transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                >
                  Android
                </TabsTrigger>
                <TabsTrigger
                  value="iphone"
                  className="rounded-lg text-xs sm:text-sm font-semibold transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                >
                  iPhone
                </TabsTrigger>
              </TabsList>

              {/* Android Tab Content */}
              <TabsContent value="android" className="pt-4 space-y-4 focus-visible:outline-none">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Compass className="h-4 w-4 text-primary" />
                  Pasang Weddly di Android
                </h3>

                <div className="space-y-3">
                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground">
                        Langkah 1 — Buka lewat Chrome
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Akses <strong className="text-foreground">weddly.my.id</strong> menggunakan browser Google Chrome di perangkat Android kamu.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground flex items-center gap-1.5 flex-wrap">
                        Langkah 2 — Temukan menu instalasi
                        <span className="inline-flex items-center text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono border">
                          <MoreVertical className="h-3 w-3 inline mr-0.5" /> 3 titik
                        </span>
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Ketuk ikon menu tiga titik (⋮) di pojok kanan atas browser, lalu cari opsi <strong className="text-foreground">&quot;Instal aplikasi&quot;</strong> atau <strong className="text-foreground">&quot;Tambahkan ke layar utama&quot;</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground">
                        Langkah 3 — Konfirmasi pemasangan
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Pilih <strong className="text-foreground">&quot;Instal&quot;</strong> atau <strong className="text-foreground">&quot;Tambahkan&quot;</strong> sesuai opsi yang muncul, lalu tunggu hingga ikon Weddly tersedia di layar utama.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      4
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground">
                        Langkah 4 — Mulai gunakan Weddly
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Ketuk ikon Weddly dari layar utama untuk membuka aplikasi dengan cepat.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* iPhone Tab Content */}
              <TabsContent value="iphone" className="pt-4 space-y-4 focus-visible:outline-none">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Compass className="h-4 w-4 text-primary" />
                  Pasang Weddly di iPhone
                </h3>

                <div className="space-y-3">
                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground">
                        Langkah 1 — Gunakan Safari
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Buka <strong className="text-foreground">weddly.my.id</strong> melalui browser Safari di iPhone.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground flex items-center gap-1.5 flex-wrap">
                        Langkah 2 — Akses menu berbagi
                        <span className="inline-flex items-center text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono border">
                          <Share className="h-3 w-3 inline mr-0.5 text-blue-500" /> Share
                        </span>
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Ketuk ikon Bagikan (Share) pada Safari, kemudian cari opsi untuk menambahkan website ke layar utama.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground flex items-center gap-1.5 flex-wrap">
                        Langkah 3 — Tambahkan Weddly
                        <span className="inline-flex items-center text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono border">
                          <PlusSquare className="h-3 w-3 inline mr-0.5" /> Add to Home Screen
                        </span>
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Pilih <strong className="text-foreground">&quot;Tambahkan ke Layar Utama&quot;</strong> (Add to Home Screen) atau <strong className="text-foreground">&quot;Open as Web App&quot;</strong> yang tersedia pada versi iOS kamu.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-card border border-border/60 flex items-start gap-3">
                    <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      4
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-foreground">
                        Langkah 4 — Selesaikan pemasangan
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Konfirmasi dengan mengetuk <strong className="text-foreground">&quot;Tambah&quot;</strong> (Add). Ikon Weddly akan muncul di layar utama iPhone kamu.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Info Box */}
            <div className="p-4 rounded-xl bg-muted/50 border border-border/70 space-y-1.5">
              <div className="flex items-center gap-2 font-bold text-xs text-foreground">
                <Info className="h-4 w-4 text-primary shrink-0" />
                <span>Lebih dekat dengan setiap rencana.</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed pl-6">
                Weddly dapat ditambahkan ke layar utama agar kamu tidak perlu mengetik alamat website setiap kali ingin melanjutkan persiapan pernikahan. Ketersediaan fitur aplikasi dapat bergantung pada perangkat dan browser yang digunakan.
              </p>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="p-4 border-t border-border/50 bg-background/95 backdrop-blur-sm flex justify-end shrink-0">
            <Button
              type="button"
              onClick={() => setModalOpen(false)}
              className="w-full sm:w-auto rounded-full px-8 py-2 font-semibold text-xs sm:text-sm bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
            >
              Oke, Mengerti
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
